// ─────────────────────────────────────────────────────
// SXT Portal — Root App with Sidebar Layout + Auth Guard
// TryOn renders full-screen (no sidebar, no padding)
// ─────────────────────────────────────────────────────
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Sidebar } from "./components/Sidebar";
import { DashboardPage } from "./pages/Dashboard";
import { WardrobePage } from "./pages/Wardrobe";
import { TryOnPage } from "./pages/TryOn";
import { AnalyticsPage } from "./pages/Analytics";
import { StylePage } from "./pages/Style";
import { SocialPage } from "./pages/Social";
import { SettingsPage } from "./pages/Settings";
import { LoginPage } from "./pages/Login";
import { OBJToGLBConverterComponent } from "./components/OBJToGLBConverter";

function RequireAuth({ children }: { children: React.ReactNode }) {
  const token = localStorage.getItem("sxt_token");
  const location = useLocation();
  if (!token) {
    // Preserve where they were trying to go — send them to login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return <>{children}</>;
}

// After login, redirect to wherever they came from (or dashboard)
function LoginWithRedirect() {
  const token = localStorage.getItem("sxt_token");
  const location = useLocation();
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || "/dashboard";
  if (token) return <Navigate to={from} replace />;
  return <LoginPage />;
}

// Shell layout — sidebar + scrollable main (all pages except TryOn)
function ShellLayout() {
  return (
    <div className="flex h-screen overflow-hidden">
      <Sidebar />
      <main className="flex-1 overflow-y-auto p-8">
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/wardrobe" element={<WardrobePage />} />
          <Route path="/analytics" element={<AnalyticsPage />} />
          <Route path="/style" element={<StylePage />} />
          <Route path="/social" element={<SocialPage />} />
          <Route path="/settings" element={<SettingsPage />} />
        </Routes>
      </main>
    </div>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginWithRedirect />} />
      {/* Root → go to dashboard if authed, login if not */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />

      {/* Internal tool — OBJ→GLB converter (not in sidebar, admin only) */}
      <Route
        path="/tools/converter"
        element={
          <RequireAuth>
            <div className="min-h-screen bg-gray-100 flex items-center justify-center p-8">
              <OBJToGLBConverterComponent />
            </div>
          </RequireAuth>
        }
      />

      {/* TryOn is full-screen — no sidebar, no padding */}
      <Route
        path="/tryon"
        element={
          <RequireAuth>
            <TryOnPage />
          </RequireAuth>
        }
      />

      {/* All other pages use the sidebar shell */}
      <Route
        path="/*"
        element={
          <RequireAuth>
            <ShellLayout />
          </RequireAuth>
        }
      />
    </Routes>
  );
}
